import { useEffect, useState } from "react";
import { queryClient } from "@/lib/queryClient";
import { useStations } from "@/hooks/useStations";
import { useAuthContext } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AutoGrowTextarea from "./auto-grow-textarea";
import { MessageSquare, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import CopyChatPanel from "./copy-chat-panel";
import type { Station } from "@shared/schema";
import type { Sponsor } from "@shared/schema";

/**
 * Workbench-embedded copy generation, styled to the workbench design language.
 * Calls the SAME station-scoped generate-initial SSE endpoint as the classic
 * Copy Engine and tags results with sponsorId.
 */

interface CopyToolProps {
  sponsor: Sponsor;
}

const UNDERLINE_INPUT = "border-0 border-b border-gray-100 rounded-none px-0 focus-visible:ring-0 focus-visible:border-emerald-400 text-[15px] placeholder:text-gray-300";

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 block mb-0.5">
      {children}
    </label>
  );
}

export default function CopyTool({ sponsor }: CopyToolProps) {
  const { getAuthToken } = useAuthContext();
  const activityKey = `/api/sponsors/${sponsor.id}/activity`;

  // Role-aware station list: admins see all, regular users only assigned.
  const { data: stations = [] } = useStations();

  const [stationId, setStationId] = useState<string>("");
  const [copyType, setCopyType] = useState<"broadcast" | "event">("broadcast");

  // Most users have exactly one assigned station; preselect it so Generate is
  // immediately available. Never overrides a choice already made.
  useEffect(() => {
    if (!stationId && stations.length === 1) {
      setStationId(stations[0].id);
    }
  }, [stations, stationId]);
  // Broadcast
  const [previousCopy, setPreviousCopy] = useState("");
  const [moreInfoLink, setMoreInfoLink] = useState("");
  // Event
  const [eventName, setEventName] = useState("");
  const [eventWebsite, setEventWebsite] = useState("");
  const [eventDatesAndTimes, setEventDatesAndTimes] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [ticketsLink, setTicketsLink] = useState("");
  // Shared
  const [additionalDetails, setAdditionalDetails] = useState("");

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  // Context for the refinement chat: the station and form values used for
  // the generation. genId keys the chat panel so a new run resets the thread.
  // copyMessageId is the saved copy_messages row the chat transcript is
  // persisted onto.
  const [chatMeta, setChatMeta] = useState<{ station: Station; formData: Record<string, string>; genId: number; copyMessageId: string | null } | null>(null);

  const eventReady = copyType !== "event" || (!!eventLocation.trim() && !!ticketsLink.trim());
  const canGenerate = !!stationId && eventReady && !generating;

  const generate = async () => {
    setError(null);
    setResult(null);
    setGenerating(true);
    setProgress("Starting copy generation...");
    try {
      const values: Record<string, string> = {
        sponsorName: sponsor.name,
        copyType,
        sponsorId: sponsor.id,
      };
      if (copyType === "broadcast") {
        if (sponsor.website) values.sponsorWebsite = sponsor.website;
        if (previousCopy.trim()) values.previousCopy = previousCopy.trim();
        if (moreInfoLink.trim()) values.moreInfoLink = moreInfoLink.trim();
      } else {
        if (eventName.trim()) values.eventName = eventName.trim();
        if (eventWebsite.trim()) values.eventWebsite = eventWebsite.trim();
        if (eventDatesAndTimes.trim()) values.eventDatesAndTimes = eventDatesAndTimes.trim();
        if (eventLocation.trim()) values.eventLocation = eventLocation.trim();
        if (ticketsLink.trim()) values.ticketsLink = ticketsLink.trim();
      }
      if (additionalDetails.trim()) values.additionalDetails = additionalDetails.trim();

      const token = await getAuthToken();
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      const response = await fetch(`/api/stations/${stationId}/copy-engine/generate-initial`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(values),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error("Copy generation failed");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");
      const decoder = new TextDecoder();
      let buffer = "";
      let initialMessage: string | null = null;
      let copyMessageId: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === "progress") {
              setProgress(parsed.detail || parsed.stage || "Working...");
            } else if (parsed.type === "complete") {
              initialMessage = parsed.initialMessage ?? null;
              copyMessageId = parsed.copyMessageId ?? null;
            } else if (parsed.type === "error") {
              throw new Error(parsed.message || "Copy generation failed");
            }
          } catch (e) {
            if (e instanceof Error && !e.message.includes("JSON")) throw e;
          }
        }
      }

      if (!initialMessage) throw new Error("No copy received from the server");
      setResult(initialMessage);
      const stationObj = stations.find((s) => s.id === stationId);
      if (stationObj) {
        setChatMeta((prev) => ({ station: stationObj, formData: values, genId: (prev?.genId ?? 0) + 1, copyMessageId }));
      }
      queryClient.invalidateQueries({ queryKey: [activityKey] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
    } catch (err) {
      setError((err as Error).message || "Copy generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="relative overflow-hidden rounded-xl bg-white shadow-sm border border-gray-100 p-6 sm:p-8">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />

      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
          <MessageSquare className="h-[18px] w-[18px]" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Copy Engine</p>
          <h3 className="text-lg font-semibold text-gray-900 leading-snug">Generate copy for {sponsor.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Uses the station's Copy Engine with this sponsor prefilled. Saved to this sponsor's history.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-5">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="max-w-3xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <FieldLabel>Partner Station <span className="text-red-400 normal-case">*</span></FieldLabel>
            <Select value={stationId} onValueChange={setStationId}>
              <SelectTrigger className="mt-1" data-testid="select-copy-station">
                <SelectValue placeholder="Select a station" />
              </SelectTrigger>
              <SelectContent>
                {stations.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Copy Type</FieldLabel>
            <Select value={copyType} onValueChange={(v) => setCopyType(v as "broadcast" | "event")}>
              <SelectTrigger className="mt-1" data-testid="select-copy-tool-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="broadcast">Broadcast</SelectItem>
                <SelectItem value="event">Event</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {copyType === "broadcast" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <FieldLabel htmlFor="ct-moreinfo">More Info Link (Link to be used in Copy)</FieldLabel>
              <Input
                id="ct-moreinfo"
                placeholder="Example.com/vanity-url"
                value={moreInfoLink}
                onChange={(e) => setMoreInfoLink(e.target.value)}
                className={UNDERLINE_INPUT}
                data-testid="input-copy-tool-more-info"
              />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="ct-previous">Previously Used Copy (if applicable)</FieldLabel>
              <AutoGrowTextarea
                id="ct-previous"
                value={previousCopy}
                onChange={setPreviousCopy}
                placeholder="Paste any copy they have run before..."
                focusColor="focus:border-emerald-400"
                testId="input-copy-tool-previous"
              />
            </div>
          </div>
        )}

        {copyType === "event" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <FieldLabel htmlFor="ct-event-name">Event Name</FieldLabel>
              <Input id="ct-event-name" placeholder="e.g., Summer Jazz Festival" value={eventName} onChange={(e) => setEventName(e.target.value)} className={UNDERLINE_INPUT} data-testid="input-copy-tool-event-name" />
            </div>
            <div>
              <FieldLabel htmlFor="ct-event-website">Event Website</FieldLabel>
              <Input id="ct-event-website" placeholder="event.com" value={eventWebsite} onChange={(e) => setEventWebsite(e.target.value)} className={UNDERLINE_INPUT} data-testid="input-copy-tool-event-website" />
            </div>
            <div>
              <FieldLabel htmlFor="ct-event-dates">Event Dates and Times</FieldLabel>
              <Input id="ct-event-dates" placeholder="e.g., Sat Aug 16, 6-10pm" value={eventDatesAndTimes} onChange={(e) => setEventDatesAndTimes(e.target.value)} className={UNDERLINE_INPUT} data-testid="input-copy-tool-event-dates" />
            </div>
            <div>
              <FieldLabel htmlFor="ct-event-location">Event Location <span className="text-red-400 normal-case">*</span></FieldLabel>
              <Input id="ct-event-location" placeholder="e.g., Riverside Park" value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} className={UNDERLINE_INPUT} data-testid="input-copy-tool-event-location" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="ct-tickets">Tickets / Info Link <span className="text-red-400 normal-case">*</span></FieldLabel>
              <Input id="ct-tickets" placeholder="https://event.com/tickets" value={ticketsLink} onChange={(e) => setTicketsLink(e.target.value)} className={UNDERLINE_INPUT} data-testid="input-copy-tool-tickets" />
            </div>
          </div>
        )}

        <div>
          <FieldLabel htmlFor="ct-details">Additional Details to Include</FieldLabel>
          <AutoGrowTextarea
            id="ct-details"
            value={additionalDetails}
            onChange={setAdditionalDetails}
            placeholder="Anything the copy should reflect..."
            focusColor="focus:border-emerald-400"
            testId="input-copy-tool-details"
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          {copyType === "event" && !eventReady && (
            <span className="text-xs text-gray-400">Event Location and Tickets link are required for event copy.</span>
          )}
          <Button
            onClick={generate}
            disabled={!canGenerate}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="button-generate-copy-tool"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {generating ? "Generating..." : "Generate Copy"}
          </Button>
        </div>

        {generating && (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50/60 px-4 py-5 text-sm text-emerald-700">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            {progress}
          </div>
        )}

        {result && chatMeta && (
          <CopyChatPanel
            key={chatMeta.genId}
            station={chatMeta.station}
            initialMessage={result}
            formData={chatMeta.formData}
            copyMessageId={chatMeta.copyMessageId}
            onNewCopy={() => setResult(null)}
          />
        )}
      </div>
    </section>
  );
}
